import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw, Settings, Trash2, ShieldCheck, AlertTriangle, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function cleanGraphError(err) {
  if (!err) return "";
  const m = /\{[\s\S]*\}/.exec(err);
  if (m) {
    try {
      const j = JSON.parse(m[0]);
      const e = j.error || j;
      if (e && (e.code || e.message)) {
        return [e.code, e.message].filter(Boolean).join(": ");
      }
    } catch { /* fall through */ }
  }
  return err.length > 200 ? err.slice(0, 200) + "…" : err;
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function Licenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [data, setData] = useState({ configured: false, skus: [], total_seats: 0, total_assigned: 0, total_available: 0, mfa_gaps: [], mfa_sync_status: null, mfa_sync_error: null, mfa_synced_at: null });
  const [settings, setSettings] = useState({ tenant_id: "", client_id: "", configured: false, last_synced_at: null, last_sync_status: null, last_sync_error: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({ tenant_id: "", client_id: "", client_secret: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/m365/settings"),
        api.get("/m365/licenses"),
      ]);
      setSettings(s.data);
      setData(l.data);
      setForm({ tenant_id: s.data.tenant_id || "", client_id: s.data.client_id || "", client_secret: "" });
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load M365 license data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const { data: result } = await api.post("/m365/sync");
      toast.success(`Synced · ${result.sku_count} license SKU${result.sku_count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const onSaveSettings = async () => {
    const tenant_id = form.tenant_id.trim();
    const client_id = form.client_id.trim();
    const client_secret = form.client_secret.trim();
    if (!tenant_id || !client_id) {
      toast.error("Tenant ID and Client ID are required");
      return;
    }
    if (!settings.configured && !client_secret) {
      toast.error("Client Secret is required for initial setup");
      return;
    }
    try {
      const payload = { tenant_id, client_id };
      if (client_secret) payload.client_secret = client_secret;
      await api.put("/m365/settings", payload);
      toast.success("Microsoft 365 credentials saved");
      setSettingsOpen(false);
      setForm({ ...form, client_secret: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    }
  };

  const onDeleteSettings = async () => {
    try {
      await api.delete("/m365/settings");
      toast.success("Microsoft 365 credentials removed");
      setConfirmDelete(false);
      await load();
    } catch (e) {
      toast.error("Failed to remove credentials");
    }
  };

  if (!isAdmin) {
    return (
      <div data-testid="licenses-page" className="p-6">
        <PageHeader eyebrow="[06] Licensing" title="Licenses" subtitle="Restricted to admins." />
        <div className="p-6 font-mono text-xs text-[#7A7A7A]">You don&apos;t have permission to view this page.</div>
      </div>
    );
  }

  const lastSyncBad = settings.last_sync_status === "failed";

  return (
    <div data-testid="licenses-page">
      <PageHeader
        testid="licenses-header"
        eyebrow="[06] Licensing"
        title="Microsoft 365 Licenses"
        subtitle="Live snapshot of subscribed SKUs from your organization's Microsoft 365 tenant."
        actions={
          <>
            <Button
              variant="outline"
              data-testid="m365-settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
            >
              <Settings className="w-3.5 h-3.5 mr-2" />{settings.configured ? "Connection" : "Connect"}
            </Button>
            <Button
              data-testid="m365-sync-btn"
              onClick={onSync}
              disabled={!settings.configured || syncing}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Now"}
            </Button>
          </>
        }
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Status / KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Status" value={
            settings.configured
              ? (lastSyncBad ? "Sync error" : (settings.last_sync_status === "success" ? "Connected" : "Configured"))
              : "Not connected"
          } tone={settings.configured ? (lastSyncBad ? "warn" : "ok") : "muted"} testid="kpi-status" />
          <Kpi label="Last Sync" value={formatTimestamp(settings.last_synced_at)} testid="kpi-last-sync" />
          <Kpi label="Total Seats" value={data.total_seats?.toLocaleString?.() ?? "—"} testid="kpi-total-seats" />
          <Kpi label="Available" value={data.total_available?.toLocaleString?.() ?? "—"} tone={data.total_available === 0 && data.total_seats > 0 ? "warn" : "default"} testid="kpi-available" />
        </div>

        {lastSyncBad && settings.last_sync_error && (
          <div data-testid="m365-sync-error" className="border border-[#E0B8A5] bg-[#FFF4EE] px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5" strokeWidth={1.75} />
            <div className="flex-1 min-w-0">
              <div className="uppercase-label text-[#B45309]">Last sync failed</div>
              <div className="font-mono text-xs text-[#3A3A3A] mt-1 break-words">{settings.last_sync_error}</div>
            </div>
          </div>
        )}

        {/* Empty / not-configured state */}
        {!settings.configured && (
          <div data-testid="m365-empty-state" className="border border-[#E5E0D5] bg-white px-6 py-10 text-center space-y-4">
            <ShieldCheck className="w-8 h-8 text-[#5A5A5A] mx-auto" strokeWidth={1.5} />
            <div className="space-y-1">
              <div className="text-[#0F0F0F] font-medium">Connect Microsoft 365</div>
              <div className="text-sm text-[#5A5A5A] max-w-md mx-auto">Register an Azure AD app in your tenant with the <span className="font-mono">Organization.Read.All</span> application permission, grant admin consent, and enter the credentials here to see your licenses.</div>
            </div>
            <Button
              data-testid="m365-empty-connect-btn"
              onClick={() => setSettingsOpen(true)}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Settings className="w-3.5 h-3.5 mr-2" />Connect Microsoft 365
            </Button>
            <a
              href="https://learn.microsoft.com/en-us/graph/auth-register-app-v2"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-mono text-[#5A5A5A] hover:text-[#0F0F0F] underline-offset-2 hover:underline"
            >
              Azure app registration guide <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* License table */}
        {settings.configured && (
          <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="m365-licenses-table">
              <thead>
                <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                  <Th>License (SKU)</Th>
                  <Th>SKU Part Number</Th>
                  <Th align="right">Total</Th>
                  <Th align="right">Assigned</Th>
                  <Th align="right">Available</Th>
                  <Th align="right">Utilization</Th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>
                )}
                {!loading && data.skus.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center">
                    <div className="font-mono text-xs text-[#7A7A7A]">No licenses cached. Click <strong>Sync Now</strong> to pull from Microsoft 365.</div>
                  </td></tr>
                )}
                {data.skus.map((s) => {
                  const pct = s.total > 0 ? Math.round((s.assigned / s.total) * 100) : 0;
                  return (
                    <tr key={s.sku_id} data-testid={`license-row-${s.sku_part_number}`} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors">
                      <Td><span className="text-[#0F0F0F]">{s.display_name}</span></Td>
                      <Td><span className="font-mono text-xs text-[#5A5A5A]">{s.sku_part_number}</span></Td>
                      <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{s.total.toLocaleString()}</span></Td>
                      <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{s.assigned.toLocaleString()}</span></Td>
                      <Td align="right"><span className={`font-mono text-xs ${s.available === 0 && s.total > 0 ? "text-[#B45309]" : "text-[#3A3A3A]"}`}>{s.available.toLocaleString()}</span></Td>
                      <Td align="right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-1.5 bg-[#E5E0D5] relative">
                            <div
                              className={`absolute left-0 top-0 h-full ${pct >= 100 ? "bg-[#B45309]" : pct >= 80 ? "bg-[#1A1A1A]" : "bg-[#2D7A4F]"}`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs text-[#5A5A5A] w-10 text-right">{pct}%</span>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Licensed users without MFA */}
        {settings.configured && (
          <div className="space-y-3" data-testid="mfa-gaps-section">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#B45309]" strokeWidth={1.75} />
              <h2 className="text-[#0F0F0F] font-medium tracking-tight">Licensed users without MFA</h2>
              {data.mfa_sync_status === "success" && (
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A] bg-[#F0EDE3] border border-[#E5E0D5] px-2 py-0.5" data-testid="mfa-gaps-count">
                  {data.mfa_gaps.length} {data.mfa_gaps.length === 1 ? "user" : "users"}
                </span>
              )}
            </div>
            <p className="text-sm text-[#5A5A5A] max-w-2xl">
              Enabled users with at least one Microsoft 365 license who are <strong>not registered for MFA</strong>.
              Data comes from Microsoft&apos;s authentication methods report and can lag real changes by up to ~36 hours.
            </p>

            {data.mfa_sync_status === "failed" && (
              <div data-testid="mfa-gaps-error" className="border border-[#E0B8A5] bg-[#FFF4EE] px-5 py-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="uppercase-label text-[#B45309]">Couldn&apos;t read MFA data</div>
                  <div className="text-sm text-[#3A3A3A] mt-1">
                    Add the Microsoft Graph <span className="font-mono">User.Read.All</span> and <span className="font-mono">AuditLog.Read.All</span> <strong>application</strong> permissions to your Azure app, click <strong>Grant admin consent</strong>, then Sync again.
                  </div>
                  {data.mfa_sync_error && <div className="font-mono text-xs text-[#7A7A7A] mt-2 break-words">{cleanGraphError(data.mfa_sync_error)}</div>}
                </div>
              </div>
            )}

            {data.mfa_sync_status === "success" && data.mfa_gaps.length === 0 && (
              <div data-testid="mfa-gaps-empty" className="border border-[#CBE3D2] bg-[#F1FAF4] px-5 py-4 flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-[#2D7A4F]" strokeWidth={1.75} />
                <span className="text-sm text-[#2D5A42]">All licensed, enabled users are registered for MFA. Nice.</span>
              </div>
            )}

            {data.mfa_sync_status === "success" && data.mfa_gaps.length > 0 && (
              <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="mfa-gaps-table">
                  <thead>
                    <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                      <Th>Display Name</Th>
                      <Th>User Principal Name</Th>
                      <Th align="right">Licenses</Th>
                      <Th align="right">MFA Capable</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mfa_gaps.map((u) => (
                      <tr key={u.id} data-testid={`mfa-gap-row-${u.id}`} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors">
                        <Td><span className="text-[#0F0F0F]">{u.display_name || "—"}</span></Td>
                        <Td><span className="font-mono text-xs text-[#5A5A5A]">{u.user_principal_name}</span></Td>
                        <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{u.license_count}</span></Td>
                        <Td align="right">
                          <span className={`font-mono text-[10px] uppercase tracking-wider ${u.is_mfa_capable ? "text-[#B45309]" : "text-[#B91C1C]"}`}>
                            {u.is_mfa_capable ? "Capable" : "Not capable"}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!data.mfa_sync_status && (
              <div data-testid="mfa-gaps-pending" className="border border-[#E5E0D5] bg-white px-5 py-4 font-mono text-xs text-[#7A7A7A]">
                Click <strong>Sync Now</strong> to check which licensed users are missing MFA.
              </div>
            )}
          </div>
        )}

        {settings.configured && (
          <div className="font-mono text-xs text-[#9A9A9A] flex items-center justify-between">
            <span>Total SKUs: {data.skus.length} · Synced: {formatTimestamp(settings.last_synced_at)}</span>
            <button
              onClick={() => setConfirmDelete(true)}
              data-testid="m365-disconnect-btn"
              className="text-[#7A7A7A] hover:text-[#B91C1C] inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3 h-3" /> Disconnect tenant
            </button>
          </div>
        )}
      </div>

      {/* Settings modal */}
      <AlertDialog open={settingsOpen} onOpenChange={(o) => !o && setSettingsOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Microsoft 365 Connection</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              Credentials are stored encrypted and used only to read license counts from Microsoft Graph (<span className="font-mono">/subscribedSkus</span>).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="uppercase-label">Tenant ID (Directory ID)</Label>
              <Input
                data-testid="m365-tenant-id-input"
                value={form.tenant_id}
                onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="uppercase-label">Client ID (Application ID)</Label>
              <Input
                data-testid="m365-client-id-input"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div>
              <Label className="uppercase-label">Client Secret {settings.configured && <span className="ml-1 normal-case tracking-normal text-[#7A7A7A]">(leave blank to keep existing)</span>}</Label>
              <Input
                data-testid="m365-client-secret-input"
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={settings.configured ? "•••••••••••••" : "secret value (not the secret ID)"}
                className="rounded-none bg-white border-[#E5E0D5] font-mono text-sm mt-1"
              />
            </div>
            <div className="text-xs text-[#7A7A7A] font-mono leading-relaxed border border-[#E5E0D5] bg-[#FAF7F0] px-3 py-2">
              In Azure: App registrations → API permissions → Add Microsoft Graph <strong>application</strong> permission <span className="font-mono">Organization.Read.All</span>, then click <strong>Grant admin consent</strong>.
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="m365-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="m365-save-btn" onClick={onSaveSettings} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disconnect confirmation */}
      <AlertDialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Disconnect Microsoft 365?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">This removes the stored credentials and cached license data for this organization. You can reconnect at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="m365-cancel-disconnect-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="m365-confirm-disconnect-btn" onClick={onDeleteSettings} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium`}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td className={`px-4 py-3 text-${align}`}>{children}</td>;
}
function Kpi({ label, value, tone = "default", testid }) {
  const valueClass =
    tone === "ok" ? "text-[#2D7A4F]"
    : tone === "warn" ? "text-[#B45309]"
    : tone === "muted" ? "text-[#9A9A9A]"
    : "text-[#0F0F0F]";
  return (
    <div data-testid={testid} className="border border-[#E5E0D5] bg-white px-4 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A]">{label}</div>
      <div className={`mt-1 font-mono text-lg ${valueClass}`}>{value}</div>
    </div>
  );
}
